import React from "react";

import {
  BarChart3,
  Check,
  ChevronDown,
  Circle,
  Grid2x2,
  LayoutList,
  Lock,
  Pause,
  PencilRuler,
  Plus,
  Search,
  Trash2,
} from "lucide-react";

import { SORT_OPTIONS } from "../constants/dashboardSortConstants";
import { STATUS_FILTER_OPTIONS } from "../constants/dashboardStatusConstants";
import { formatCount, formatPercentage } from "../utils/dashboardUtils";

import type {
  DashboardStatusFilter,
  ExperimentData,
  ExperimentStatus,
  SortOption,
  ViewMode,
} from "../types/dashboard.types";

interface DashboardExperimentBrowserProps {
  isLoading: boolean;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  sortBy: SortOption;
  onSortChange: (sortBy: SortOption) => void;
  statusFilter: DashboardStatusFilter;
  onStatusChange: (status: DashboardStatusFilter) => void;
  statusCounts: Record<ExperimentStatus, number>;
  experiments: ExperimentData[];
  currentPage: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  cardsLoaded: boolean;
  onNewExperiment: () => void;
  onExperimentClick: (experiment: ExperimentData) => void;
  onDeleteExperiment: (experiment: ExperimentData) => void;
  deletingExperimentId?: string | null;
}

const statusClassMap: Record<ExperimentStatus, string> = {
  draft: "draft",
  locked: "locked",
  active: "active",
  paused: "paused",
  completed: "completed",
  deleted: "deleted",
};

const renderStatusIcon = (iconKey: "all" | ExperimentStatus) => {
  switch (iconKey) {
    case "all":
      return <BarChart3 size={17} />;
    case "draft":
      return <PencilRuler size={17} />;
    case "locked":
      return <Lock size={17} />;
    case "active":
      return <Circle size={17} fill="currentColor" />;
    case "paused":
      return <Pause size={17} />;
    case "completed":
      return <Check size={17} />;
    case "deleted":
      return <Trash2 size={17} />;
    default:
      return null;
  }
};

const handleActivateWithKeyboard = (
  event: React.KeyboardEvent<HTMLElement>,
  callback: () => void,
) => {
  if (event.key === "Enter" || event.key === " ") {
    event.preventDefault();
    callback();
  }
};

export const DashboardExperimentBrowser: React.FC<
  DashboardExperimentBrowserProps
> = ({
  searchQuery,
  onSearchChange,
  viewMode,
  onViewModeChange,
  sortBy,
  onSortChange,
  statusFilter,
  onStatusChange,
  statusCounts,
  experiments,
  currentPage,
  totalItems,
  itemsPerPage,
  onPageChange,
  cardsLoaded,
  onNewExperiment,
  onExperimentClick,
  onDeleteExperiment,
  deletingExperimentId,
  isLoading,
}) => {
  const totalStatusCount = Object.values(statusCounts).reduce(
    (sum, count) => sum + count,
    0,
  );
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const pageNumbers = Array.from(
    { length: totalPages },
    (_, index) => index + 1,
  );

  return (
    <section className="innovation-dashboard__browser">
      <div className="innovation-dashboard__status-filter-row">
        {STATUS_FILTER_OPTIONS.map((option) => {
          const count =
            option.value === "all"
              ? totalStatusCount
              : statusCounts[option.value];
          const isActive = statusFilter === option.value;

          return (
            <button
              key={option.value}
              type="button"
              className={`innovation-dashboard__status-filter-card${isActive ? " is-active" : ""}`}
              onClick={() => onStatusChange(option.value)}
            >
              <span className="innovation-dashboard__status-filter-icon">
                {renderStatusIcon(option.iconKey)}
              </span>
              <span className="innovation-dashboard__status-filter-label">
                {option.label}
              </span>
              <strong className="innovation-dashboard__status-filter-count">
                {count}
              </strong>
            </button>
          );
        })}
      </div>

      <div className="innovation-dashboard__browser-toolbar">
        <label className="innovation-dashboard__search">
          <Search size={16} />
          <input
            value={searchQuery}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder="Search experiments by name, tag, or owner..."
          />
        </label>

        <div className="innovation-dashboard__toolbar-actions">
          <label className="innovation-dashboard__sort">
            <span className="innovation-dashboard__sort-icon">
              <BarChart3 size={14} />
            </span>
            <select
              value={sortBy}
              onChange={(event) =>
                onSortChange(event.target.value as SortOption)
              }
            >
              {SORT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <ChevronDown size={14} />
          </label>

          <div
            className="innovation-dashboard__view-toggle"
            role="group"
            aria-label="View mode"
          >
            <button
              type="button"
              className={viewMode === "list" ? "is-active" : ""}
              onClick={() => onViewModeChange("list")}
              aria-label="List view"
            >
              <LayoutList size={16} />
            </button>
            <button
              type="button"
              className={viewMode === "grid" ? "is-active" : ""}
              onClick={() => onViewModeChange("grid")}
              aria-label="Grid view"
            >
              <Grid2x2 size={16} />
            </button>
          </div>

          <button
            type="button"
            className="innovation-dashboard__new-button"
            onClick={onNewExperiment}
          >
            <Plus size={16} />
            <span>New Experiment</span>
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="innovation-dashboard__loading-state innovation-dashboard__loading-state--browser">
          <div
            className="innovation-dashboard__loading-spinner"
            aria-hidden="true"
          />
          <div className="innovation-dashboard__loading-title">
            Loading experiments
          </div>
          <p className="innovation-dashboard__loading-copy">
            Pulling the latest experiment list and live metrics from Falcon.
          </p>
        </div>
      ) : experiments.length === 0 ? (
        <div className="innovation-dashboard__empty-state">
          <div className="innovation-dashboard__empty-state-icon">
            <Search size={18} />
          </div>
          <div className="innovation-dashboard__empty-state-title">
            No experiments match the selected filters
          </div>
          <p className="innovation-dashboard__empty-state-copy">
            Adjust the search or status filter to widen the list.
          </p>
        </div>
      ) : viewMode === "grid" ? (
        <div className="innovation-dashboard__experiment-grid">
          {experiments.map((experiment) => {
            const isDeleting = deletingExperimentId === experiment.id;

            return (
              <div
                key={experiment.id}
                className={`innovation-dashboard__experiment-card ${statusClassMap[experiment.status]}${experiment.highlight ? " is-highlighted" : ""}${cardsLoaded ? " is-visible" : ""}`}
                onClick={() => onExperimentClick(experiment)}
                onKeyDown={(event) =>
                  handleActivateWithKeyboard(event, () =>
                    onExperimentClick(experiment),
                  )
                }
                role="button"
                tabIndex={0}
              >
                <div className="innovation-dashboard__card-top">
                  <span
                    className={`innovation-dashboard__status-pill innovation-dashboard__status-pill--${statusClassMap[experiment.status]}`}
                  >
                    {experiment.status}
                  </span>
                  <div className="innovation-dashboard__card-actions">
                    <span className="innovation-dashboard__owner-badge">
                      {experiment.owner.initials}
                    </span>
                    <button
                      type="button"
                      className="innovation-dashboard__action-button innovation-dashboard__action-button--danger"
                      aria-label={`Delete ${experiment.name}`}
                      title="Delete experiment"
                      disabled={isDeleting}
                      onClick={(event) => {
                        event.stopPropagation();
                        onDeleteExperiment(experiment);
                      }}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>

                <div className="innovation-dashboard__card-title">
                  {experiment.name}
                </div>
                <div className="innovation-dashboard__card-code">
                  {experiment.code}
                </div>

                <div className="innovation-dashboard__card-stats">
                  <span>{formatCount(experiment.metrics.responses)}</span>
                  <span>
                    {formatPercentage(experiment.metrics.completionRate)}
                  </span>
                </div>

                <div className="innovation-dashboard__card-divider" />

                <div className="innovation-dashboard__card-footer">
                  <span>{experiment.updatedLabel}</span>
                  <strong>{experiment.owner.shortName}</strong>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="innovation-dashboard__table-shell">
          <div className="innovation-dashboard__table-header">
            <span>Name</span>
            <span>Status</span>
            <span>Product</span>
            <span>Responses</span>
            <span>PM</span>
            <span>Updated</span>
            <span />
          </div>
          <div className="innovation-dashboard__table-body">
            {experiments.map((experiment) => {
              const isDeleting = deletingExperimentId === experiment.id;

              return (
                <div
                  key={experiment.id}
                  className={`innovation-dashboard__table-row${cardsLoaded ? " is-visible" : ""}`}
                  onClick={() => onExperimentClick(experiment)}
                  onKeyDown={(event) =>
                    handleActivateWithKeyboard(event, () =>
                      onExperimentClick(experiment),
                    )
                  }
                  role="button"
                  tabIndex={0}
                >
                  <span className="innovation-dashboard__table-name">
                    <strong>{experiment.name}</strong>
                    <small>{experiment.code}</small>
                  </span>
                  <span>
                    <span
                      className={`innovation-dashboard__status-pill innovation-dashboard__status-pill--${statusClassMap[experiment.status]}`}
                    >
                      {experiment.status}
                    </span>
                  </span>
                  <span className="innovation-dashboard__table-product">
                    <i
                      className="innovation-dashboard__product-dot"
                      style={{ backgroundColor: experiment.productColor }}
                    />
                    {experiment.product}
                  </span>
                  <span className="innovation-dashboard__table-responses">
                    {formatCount(experiment.metrics.responses)}/
                    {formatCount(experiment.metrics.targetResponses)}
                  </span>
                  <span>{experiment.owner.shortName}</span>
                  <span>{experiment.updatedLabel}</span>
                  <span className="innovation-dashboard__table-actions">
                    <button
                      type="button"
                      className="innovation-dashboard__action-button innovation-dashboard__action-button--danger"
                      aria-label={`Delete ${experiment.name}`}
                      title="Delete experiment"
                      disabled={isDeleting}
                      onClick={(event) => {
                        event.stopPropagation();
                        onDeleteExperiment(experiment);
                      }}
                    >
                      <Trash2 size={14} />
                    </button>
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="innovation-dashboard__pagination">
        <button
          type="button"
          className="innovation-dashboard__pagination-link"
          disabled={currentPage === 1}
          onClick={() => onPageChange(currentPage - 1)}
        >
          Previous
        </button>

        {pageNumbers.map((pageNumber) => (
          <button
            key={pageNumber}
            type="button"
            className={`innovation-dashboard__pagination-page${pageNumber === currentPage ? " is-active" : ""}`}
            onClick={() => onPageChange(pageNumber)}
          >
            {pageNumber}
          </button>
        ))}

        <button
          type="button"
          className="innovation-dashboard__pagination-link"
          disabled={currentPage === totalPages}
          onClick={() => onPageChange(currentPage + 1)}
        >
          Next
        </button>
      </div>
    </section>
  );
};
